/**
 * Payment requests — the tabbed list, the row actions and the two drawers.
 *
 * The screen fetches requests, people and the pending proofs together,
 * because the tab a request belongs under is DERIVED from its money and its
 * due date, and Review needs to know whether a proof exists. Asking the API
 * for a status instead would put the same rule in two places.
 *
 * Spec: DESIGN-BRIEF.md §E and `vRequests()` in prototype.html.
 */
import React, {useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {MagnifyingGlassIcon, PlusIcon} from '@heroicons/react/24/outline';
import NetlyPayRepository from '../../../internals/repository/NetlyPayRepository';
import type {NetlyPayData, PaymentRequest} from '../../../internals/managers/NetlyPayManager';
import {
  REQUEST_EMPTY,
  REQUEST_TABS,
  formFromDraft,
  newRequestBody,
  newRequestForm,
  requestTable,
  type NewRequestForm,
  type RequestTab,
} from '../requests';
import {useNetlyPayFetch, useNetlyPayLink} from '../hooks/useNetlyPay';
import {EmptyState, ErrorState, Skeleton} from '../components/States';
import {RequestStatusChip} from '../components/StatusChip';
import {GhostButton, PrimaryButton} from '../components/Overlays';
import {ScreenGate, Toast} from '../components/ScreenGate';
import RequestDrawer from './RequestDrawer';
import NewRequestDrawer from './NewRequestDrawer';

const selectCls = 'nl-select w-auto';
const th = 'px-4 py-2.5 text-left text-[12px] font-semibold text-text3 whitespace-nowrap';
const td = 'px-4 py-3 text-[13px] text-text1 align-middle';

const rowAction =
  'h-[30px] px-2.5 rounded-md border border-borderc bg-surface text-[12px] font-medium text-text2 hover:bg-surface2 hover:text-text1 transition-colors whitespace-nowrap';

const isTab = (value: string | null): value is RequestTab =>
  !!value && REQUEST_TABS.some((t) => t.key === value);

export const PaymentRequestsView = ({selectedShop}: any) => {
  const shopId = selectedShop?.shopId;
  const currency = selectedShop?.currency ?? 'Rs.';
  const link = useNetlyPayLink(shopId);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // The Overview's Overdue card links straight to ?tab=overdue.
  const tabFromUrl = params.get('tab');
  const [tab, setTab] = useState<RequestTab>(isTab(tabFromUrl) ? tabFromUrl : 'due');
  const [q, setQ] = useState('');
  const [batchId, setBatchId] = useState('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [form, setForm] = useState<NewRequestForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2800);
  };

  const book = useNetlyPayFetch<NetlyPayData>(
    link.phase === 'linked' ? shopId : null,
    async (id) => {
      const [requests, people, pending] = await Promise.all([
        NetlyPayRepository.listRequests(id),
        NetlyPayRepository.listMembers(id),
        NetlyPayRepository.listPending(id),
      ]);
      return {
        requests: requests?.requests ?? [],
        members: people?.members ?? [],
        programs: people?.programs ?? [],
        batches: people?.batches ?? [],
        approvals: pending?.payments ?? [],
      };
    },
    [link.phase],
    (d) => (d?.requests?.length ?? 0) === 0,
  );

  const data: NetlyPayData = book.data ?? {
    requests: [],
    members: [],
    programs: [],
    batches: [],
    approvals: [],
  };

  const table = useMemo(
    () => requestTable(data, {tab, q, batchId}, undefined, currency),
    [data, tab, q, batchId, currency],
  );

  const openRequest: PaymentRequest | null = openId
    ? data.requests.find((r) => r.id === openId) ?? null
    : null;

  const pickTab = (key: RequestTab) => {
    setTab(key);
    const next = new URLSearchParams(params);
    if (key === 'due') next.delete('tab');
    else next.set('tab', key);
    setParams(next, {replace: true});
  };

  const remind = async (ids: string[], who: string) => {
    if (!shopId || !ids.length) return;
    setBusy(true);
    try {
      await NetlyPayRepository.remind(shopId, {requestIds: ids});
      flash(`Reminder sent to ${who}`);
      book.reload();
    } catch (e: any) {
      flash(e?.message ?? 'Could not send the reminder');
    } finally {
      setBusy(false);
    }
  };

  const review = (approvalId: string) => navigate(`/approvals?proof=${encodeURIComponent(approvalId)}`);

  const receipt = async (id: string) => {
    if (!shopId) return;
    try {
      const res = await NetlyPayRepository.receipt(shopId, id);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
      else flash(`Receipt ${id} is not ready yet`);
    } catch (e: any) {
      flash(e?.message ?? 'Could not fetch the receipt');
    }
  };

  const submit = async (asDraft: boolean) => {
    if (!shopId || !form) return;
    setBusy(true);
    try {
      await NetlyPayRepository.createRequest(shopId, newRequestBody(data, form, asDraft));
      if (asDraft) {
        flash('Draft saved');
        pickTab('draft');
      } else {
        flash('Requests sent');
        pickTab('due');
      }
      setForm(null);
      book.reload();
    } catch (e: any) {
      flash(e?.message ?? 'Could not send the request');
    } finally {
      setBusy(false);
    }
  };

  /**
   * No credit-note route exists on the Pay API (CONTRACT-NETLISE has no
   * /requests/{id}/credit), so there is nothing to call. The drawer's control
   * is disabled; this stays as the one place to wire it when the route lands.
   */
  const creditNote = async (_amountMinor: number, _reason: string) => {
    flash('Credit notes are not available yet');
  };

  const cancel = async (reason: string) => {
    if (!shopId || !openId) return;
    setBusy(true);
    try {
      await NetlyPayRepository.cancelRequest(shopId, openId, {reason});
      flash(`${openId} cancelled`);
      setOpenId(null);
      book.reload();
    } catch (e: any) {
      flash(e?.message ?? 'Could not cancel the request');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ScreenGate link={link} shopId={shopId} shop={selectedShop}>
      <div className="flex flex-col w-full p-7 gap-5 nl-rise">
        <div className="flex items-end justify-between gap-5 flex-wrap">
          <div>
            <h1 className="text-[26px] font-semibold text-text1 tracking-[-0.01em] leading-tight">
              Payment requests
            </h1>
            <p className="text-[13px] text-text2 mt-1 font-mono tabular-nums">
              {book.phase === 'loading' ? 'Loading…' : table.summary}
            </p>
          </div>
          <PrimaryButton onClick={() => setForm(newRequestForm({due: '', title: ''}))}>
            <span className="flex items-center gap-1.5">
              <PlusIcon className="w-4 h-4" />
              New request
            </span>
          </PrimaryButton>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 flex-wrap border-b border-borderc">
          {REQUEST_TABS.map((t) => {
            const on = tab === t.key;
            const count = table.counts[t.key] ?? 0;
            const overdue = t.key === 'overdue' && count > 0;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => pickTab(t.key)}
                aria-current={on ? 'page' : undefined}
                className={`flex items-center gap-2 px-3 pb-2.5 pt-1.5 text-[13.5px] border-b-2 -mb-px transition-colors ${
                  on ? 'border-brand text-text1 font-semibold' : 'border-transparent text-text3 hover:text-text2'
                }`}
              >
                {t.label}
                <span
                  className={`px-1.5 py-0.5 rounded-md text-[11.5px] font-mono tabular-nums font-semibold ${
                    overdue
                      ? 'bg-neg-soft text-neg'
                      : on
                      ? 'bg-brand-soft text-brand'
                      : 'bg-surface3 text-text3'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-text3 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search request, title or member"
              aria-label="Search requests"
              className={`${selectCls} pl-9 w-[290px]`}
            />
          </div>
          <select
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            aria-label="Filter by batch"
            className={selectCls}
          >
            <option value="all">All batches</option>
            {data.batches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-surface border border-borderc rounded-card overflow-hidden">
          {book.phase === 'loading' && <Skeleton rows={6} />}

          {book.phase === 'error' && <ErrorState message={book.error} onRetry={book.reload} />}

          {book.phase === 'empty' && (
            <EmptyState
              title="No requests yet"
              text="Bill a batch or a single member, and the requests appear here until they are paid."
              action={
                <PrimaryButton onClick={() => setForm(newRequestForm({due: '', title: ''}))}>
                  New request
                </PrimaryButton>
              }
            />
          )}

          {book.phase === 'ready' && table.rows.length === 0 && (
            <EmptyState
              title={q || batchId !== 'all' ? 'No requests match' : REQUEST_EMPTY[tab].title}
              text={
                q || batchId !== 'all'
                  ? 'Try another search or batch.'
                  : REQUEST_EMPTY[tab].text
              }
              action={
                q || batchId !== 'all' ? (
                  <GhostButton
                    onClick={() => {
                      setQ('');
                      setBatchId('all');
                    }}
                  >
                    Clear filters
                  </GhostButton>
                ) : undefined
              }
            />
          )}

          {book.phase === 'ready' && table.rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-surface2">
                  <tr>
                    <th className={th}>REQUEST</th>
                    <th className={th}>WHO</th>
                    <th className={th}>DUE</th>
                    <th className={th}>STATUS</th>
                    <th className={`${th} text-right`}>AMOUNT</th>
                    <th className={`${th} text-right`}>BALANCE</th>
                    <th className={`${th} text-right`}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() =>
                        row.isDraft
                          ? setForm(
                              formFromDraft(
                                data.requests.find((r) => r.id === row.id) as PaymentRequest,
                              ),
                            )
                          : setOpenId(row.id)
                      }
                      className="border-t border-borderc cursor-pointer hover:bg-surface2 transition-colors"
                    >
                      <td className={td}>
                        <span className="block font-mono text-[12px] text-text3">{row.id}</span>
                        <span className="block font-medium text-text1">
                          {row.title}
                          <span className="text-text3 font-normal">{row.repeat}</span>
                        </span>
                      </td>
                      <td className={td}>
                        <span className="flex items-center gap-2.5">
                          <span className="w-7 h-7 rounded-full bg-surface3 text-text2 text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
                            {row.initials}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-text1 truncate">{row.who}</span>
                            <span className="block text-[11.5px] text-text3 truncate">{row.whoSub}</span>
                          </span>
                        </span>
                      </td>
                      <td className={`${td} whitespace-nowrap`}>
                        <span className="block font-mono tabular-nums text-text2">{row.due}</span>
                        {row.dueNote && (
                          <span
                            className={`block text-[11.5px] font-medium ${
                              row.dueTone === 'danger' ? 'text-neg' : 'text-warn'
                            }`}
                          >
                            {row.dueNote}
                          </span>
                        )}
                      </td>
                      <td className={td}>
                        <RequestStatusChip status={row.status} />
                      </td>
                      <td className={`${td} font-mono tabular-nums text-right whitespace-nowrap`}>
                        {row.amount}
                      </td>
                      <td
                        className={`${td} font-mono tabular-nums text-right whitespace-nowrap ${
                          row.balanceTone === 'danger'
                            ? 'text-neg'
                            : row.balanceTone === 'muted'
                            ? 'text-text3'
                            : 'text-text1'
                        }`}
                      >
                        {row.balance}
                      </td>
                      <td className={`${td} text-right`} onClick={(e) => e.stopPropagation()}>
                        <span className="inline-flex items-center gap-1.5 justify-end">
                          {row.canRemind && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => remind([row.id], row.who)}
                              className={rowAction}
                            >
                              Remind
                            </button>
                          )}
                          {row.canReview && row.approvalId && (
                            <button
                              type="button"
                              onClick={() => review(row.approvalId as string)}
                              className={rowAction}
                            >
                              Review
                            </button>
                          )}
                          {row.isDraft && (
                            <button
                              type="button"
                              onClick={() =>
                                setForm(
                                  formFromDraft(
                                    data.requests.find((r) => r.id === row.id) as PaymentRequest,
                                  ),
                                )
                              }
                              className={rowAction}
                            >
                              Edit and send
                            </button>
                          )}
                          {row.isPaid && (
                            <button type="button" onClick={() => receipt(row.id)} className={rowAction}>
                              Receipt
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {openRequest && (
          <RequestDrawer
            data={data}
            request={openRequest}
            currency={currency}
            busy={busy}
            onClose={() => setOpenId(null)}
            onRemind={() => remind([openRequest.id], openRequest.title)}
            onReview={review}
            onReceipt={() => receipt(openRequest.id)}
            onCreditNote={creditNote}
            onCancel={cancel}
          />
        )}

        {form && (
          <NewRequestDrawer
            data={data}
            form={form}
            currency={currency}
            busy={busy}
            onChange={(patch) => setForm({...form, ...patch})}
            onClose={() => setForm(null)}
            onSend={() => submit(false)}
            onSaveDraft={() => submit(true)}
          />
        )}

        {toast && <Toast message={toast} />}
      </div>
    </ScreenGate>
  );
};

export default connect((state: any) => ({
  selectedShop: state.dashboard.get('selectedShop'),
}))(PaymentRequestsView);
